import React, { useEffect, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Paper,
  Typography,
  Divider,
  Stack,
  CircularProgress,
  Box,
  ApexChart,
  DateRangeMobi
} from '@esgian/esgianui';
import { useQuery } from '@tanstack/react-query';
import { getPortTrafficData } from '@api/Port';
import { toast } from 'react-toastify';
import { useTheme } from '@hooks';
import { useSelector } from 'react-redux';
import { getUser } from '@store/features';
import { checkUserHasAccess } from '@helpers';
import { useLookupOperatorVesselHierarchyQuery, useSegment } from '@hooks';
import moment from 'moment';
import { TextWithTooltipIcon } from '@components';

function PortTrafficChart({ portIds, title = 'Port Traffic' }) {
  const { theme } = useTheme();
  const user = useSelector(getUser);
  const { segmentMinDate } = useSegment();
  const [chartOptions, setChartOptions] = useState(null);
  const [chartSeries, setChartSeries] = useState([]);
  const [startDate, setStartDate] = useState(moment().subtract(29, 'days').startOf('day'));
  const [endDate, setEndDate] = useState(moment().endOf('day'));

  // Get vessels data
  const lookupOperatorVesselHierarchyQuery = useLookupOperatorVesselHierarchyQuery();

  // Get all vessel IMOs
  const allVesselImos = useMemo(() => {
    if (lookupOperatorVesselHierarchyQuery.isFetching) return [];
    return (
      lookupOperatorVesselHierarchyQuery.data?.reduce((acc, operator) => {
        const vesselImos = operator.children.map((vessel) => vessel.id);
        return [...acc, ...vesselImos];
      }, []) || []
    );
  }, [lookupOperatorVesselHierarchyQuery.data]);

  const portTrafficQuery = useQuery({
    queryKey: ['portTraffic', portIds, startDate, endDate],
    enabled: !!portIds?.length && !!startDate && !!endDate && !!allVesselImos.length,
    placeholderData: { timeline: {}, polyFit: [] },
    queryFn: ({ signal }) =>
      getPortTrafficData(
        portIds,
        signal,
        moment.utc(startDate).add(1, 'day').startOf('day'),
        moment.utc(endDate).add(1, 'day').startOf('day'),
        allVesselImos
      )
        .then((result) => {
          return result || { timeline: {}, polyFit: [] };
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            toast.error('Unable to fetch eta statistics data.', {
              position: 'top-right'
            });
          }
          return { timeline: {}, polyFit: [] };
        })
  });

  useEffect(() => {
    if (portTrafficQuery.data) {
      const { timeline, polyFit } = portTrafficQuery.data;
      const categories = [];
      const timelineValues = [];

      // Create a map to aggregate values for the same date
      const aggregatedData = new Map();

      // Sort entries by date
      const sortedEntries = Object.entries(timeline || {}).sort(
        ([dateA], [dateB]) => new Date(dateA) - new Date(dateB)
      );

      // Process and aggregate the data
      sortedEntries.forEach(([date, count]) => {
        const formattedDate = moment(date).format('DD MMM YYYY');

        if (aggregatedData.has(formattedDate)) {
          // If date exists, add to the existing count
          aggregatedData.set(formattedDate, aggregatedData.get(formattedDate) + count);
        } else {
          // If new date, set the count
          aggregatedData.set(formattedDate, count);
        }
      });

      // Convert aggregated data to arrays
      aggregatedData.forEach((count, date) => {
        categories.push(date);
        timelineValues.push(count);
      });

      // Set chart series
      setChartSeries([
        {
          name: 'Vessel Count',
          type: 'column',
          data: timelineValues
        },
        {
          name: 'Trendline',
          type: 'line',
          data: polyFit || []
        }
      ]);

      // Configure chart options
      const {
        palette: {
          mode,
          charts: { background, rowColors, twoColorsContrast }
        }
      } = theme;

      setChartOptions({
        chart: {
          height: 350,
          type: 'line',
          background: background,
          fontFamily: ['Noto Sans', 'Roboto', 'helvetica', 'Arial', 'sans-serif'].join(','),
          toolbar: {
            tools: {
              download: checkUserHasAccess(user, true)
            },
            export: {
              csv: {
                filename: 'port-traffic-data'
              },
              svg: {
                filename: 'port-traffic-data'
              },
              png: {
                filename: 'port-traffic-data'
              }
            }
          },
          stacked: false
        },
        plotOptions: {
          bar: {
            borderRadius: 2,
            columnWidth: '70%',
            dataLabels: {
              position: 'top'
            }
          }
        },
        dataLabels: {
          enabled: false
        },
        stroke: {
          width: [0, 3],
          curve: 'smooth'
        },
        grid: {
          row: {
            colors: rowColors,
            opacity: 0.5
          }
        },
        colors: [twoColorsContrast[1], twoColorsContrast[0]],
        xaxis: {
          categories: categories,
          labels: {
            rotate: -45,
            rotateAlways: true,
            formatter: function (value) {
              return value;
            }
          },
          title: {
            text: 'Date'
          }
        },
        yaxis: {
          min: 0,
          title: {
            text: 'Vessel Count'
          },
          labels: {
            formatter: function (val) {
              return Math.round(val);
            }
          }
        },
        theme: {
          mode: mode
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: {
            formatter: function (value) {
              return `${Math.round(value)} vessels`;
            }
          }
        },
        legend: {
          position: 'top',
          horizontalAlign: 'left'
        }
      });
    }
  }, [portTrafficQuery.data, theme, user]);

  const isLoading = portTrafficQuery.isFetching || lookupOperatorVesselHierarchyQuery.isFetching;

  return (
    <Paper sx={{ p: 2, height: '100%' }}>
      <Stack spacing={2}>
        <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
          <TextWithTooltipIcon
            label={<Typography variant="h6">{title}</Typography>}
            tooltipText="Daily reported ETA "
          />
          <Stack direction="row" spacing={2} alignItems="center">
            <DateRangeMobi
              sx={{
                inputProps: {
                  autoComplete: 'off',
                  label: 'Period*',
                  defaultValue: undefined,
                  value:
                    startDate || endDate
                      ? `${startDate ? moment(startDate).format('DD MMM YYYY') : ''} - ${
                          endDate ? moment(endDate).format('DD MMM YYYY') : ''
                        }`
                      : ''
                }
              }}
              onClose={({ value }) => {
                if (value[0]?.isValid()) {
                  setStartDate(value[0].startOf('day'));
                }
                if (value[1]?.isValid()) {
                  setEndDate(value[1].startOf('day'));
                }
              }}
              dateFormat="DD MMM YYYY"
              minStartDate={segmentMinDate}
              disableFuture
              startDate={startDate}
              endDate={endDate}
              placeholder={'Period'}
              dateWheels={'DD MMM YYYY'}
            />
          </Stack>
        </Stack>
        <Divider />
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ width: '100%', height: 300 }}>
            <ApexChart
              options={chartOptions || {}}
              data={chartSeries}
              type="line"
              height={300}
              loading={isLoading || !chartOptions}
            />
          </Box>
        )}
      </Stack>
    </Paper>
  );
}

PortTrafficChart.propTypes = {
  portIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  title: PropTypes.string
};

export default PortTrafficChart;
